import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { FinanceDashboardData, HRDashboardData, ReceivablesData, PayablesData, IncomeExpenseData, CashFlowData, ProfitLossData } from '@/api/types'

/**
 * Dashboard data service
 * Updated to work with real API endpoints
 */
export class DashboardsService {
  /**
   * Get finance dashboard data for specific branch
   */
  async getFinanceDashboard(branchId: number): Promise<FinanceDashboardData> {
    console.log(`📊 Fetching finance dashboard for branch ${branchId}`)
    
    const response = await apiClient.get<FinanceDashboardData>(
      endpoints.dashboards.finance(branchId)
    )
    
    console.log('📊 Finance dashboard data received:', response.data)
    return response.data
  }

  /**
   * Mock data for development when API is not available
   */
  getMockFinanceDashboard(): FinanceDashboardData {
    return {
      totalCustomers: 45,
      saudiCustomers: 32,
      nonSaudiCustomers: 13,
      totalVendors: 23,
      saudiVendors: 18,
      nonSaudiVendors: 5,
      totalQuotations: 87,
      totalQuotationAmount: 450000,
      postedInvoices: 125,
      unpostedInvoices: 12,
      postedInvoiceAmount: 280000,
      unpostedInvoiceAmount: 45000,
      totalBills: 65,
      agedReceivables: 85000,
      agedPayables: 32000,
      settledInvoices: 110,
      settledBills: 58,
    }
  }

  /**
   * Get receivables data for specific branch
   */
  async getReceivables(branchId: number): Promise<ReceivablesData> {
    console.log(`📊 Fetching receivables for branch ${branchId}`)
    
    const response = await apiClient.get<any>(
      endpoints.dashboards.receivables(branchId)
    )
    
    console.log('📊 Receivables data received:', response.data)
    
    // Handle different response structures
    let receivablesData: any = response.data
    
    // Check if data is wrapped in a 'data' property
    if (receivablesData && receivablesData.data && typeof receivablesData.data === 'object') {
      receivablesData = receivablesData.data
    }
    
    // Normalize and validate the data - round all values to integers (no decimals)
    const normalized: ReceivablesData = {
      totalReceivables: Math.round(Number(receivablesData?.totalReceivables) || 0),
      currentReceivables: Math.round(Number(receivablesData?.currentReceivables) || 0),
      overdueReceivables: Math.round(Number(receivablesData?.overdueReceivables) || 0),
    }
    
    // Ensure all values are finite numbers
    if (!isFinite(normalized.totalReceivables)) normalized.totalReceivables = 0
    if (!isFinite(normalized.currentReceivables)) normalized.currentReceivables = 0
    if (!isFinite(normalized.overdueReceivables)) normalized.overdueReceivables = 0
    
    console.log('📊 Normalized receivables data:', normalized)
    return normalized
  }

  /**
   * Get payables data for specific branch
   */
  async getPayables(branchId: number): Promise<PayablesData> {
    console.log(`📊 Fetching payables for branch ${branchId}`)
    
    const response = await apiClient.get<any>(
      endpoints.dashboards.payables(branchId)
    )
    
    console.log('📊 Payables data received:', response.data)
    
    // Handle different response structures
    let payablesData: any = response.data
    
    // Check if data is wrapped in a 'data' property
    if (payablesData && payablesData.data && typeof payablesData.data === 'object') {
      payablesData = payablesData.data
    }
    
    // Normalize and validate the data, round off decimal values
    const normalized: PayablesData = {
      totalPayables: Math.round(Number(payablesData?.totalPayables) || 0),
      currentPayables: Math.round(Number(payablesData?.currentPayables) || 0),
      overduePayables: Math.round(Number(payablesData?.overduePayables) || 0),
    }
    
    // Ensure all values are finite numbers
    if (!isFinite(normalized.totalPayables)) normalized.totalPayables = 0
    if (!isFinite(normalized.currentPayables)) normalized.currentPayables = 0
    if (!isFinite(normalized.overduePayables)) normalized.overduePayables = 0
    
    console.log('📊 Normalized payables data:', normalized)
    return normalized
  }

  /**
   * Get income and expense data for specific branch, year, and accounting method
   * Validates and normalizes data to handle NaN values and property name differences
   */
  async getIncomeExpense(branchId: number, year: number, isAccural: boolean): Promise<IncomeExpenseData> {
    console.log(`📊 Fetching income/expense for branch ${branchId}, year ${year}, isAccural: ${isAccural}`)
    
    const response = await apiClient.get<any>(
      endpoints.dashboards.incomeExpense(branchId, year, isAccural)
    )
    
    console.log('📊 RAW Income/expense data from API:', JSON.stringify(response.data, null, 2))
    
    // Handle different response structures
    let incomeExpenseData: any = response.data
    
    // Check if data is wrapped in a 'data' property
    if (incomeExpenseData && incomeExpenseData.data && typeof incomeExpenseData.data === 'object') {
      incomeExpenseData = incomeExpenseData.data
    }
    
    // Process monthly data to handle 'expenses' vs 'expense' property and remove NaN values
    let monthlyIncExp = incomeExpenseData?.monthlyIncExp || []
    
    if (Array.isArray(monthlyIncExp)) {
      monthlyIncExp = monthlyIncExp.map(item => {
        // IMPORTANT: API se 'expenses' aa raha hai, 'expense' mein convert karein
        const expenseValue = item.expenses !== undefined ? item.expenses : item.expense
        
        // Validate and clean each item
        return {
          ...item,
          month: Number(item.month) || 0,
          year: Number(item.year) || year,
          income: isFinite(Number(item.income)) && !isNaN(Number(item.income)) ? Number(item.income) : 0,
          expense: isFinite(Number(expenseValue)) && !isNaN(Number(expenseValue)) ? Number(expenseValue) : 0,
        }
      }).filter(item => 
        item.month >= 1 && item.month <= 12 && 
        isFinite(item.income) && isFinite(item.expense) &&
        !isNaN(item.income) && !isNaN(item.expense)
      )
    }
    
    const result: IncomeExpenseData = {
      totalIncome: isFinite(Number(incomeExpenseData?.totalIncome)) && !isNaN(Number(incomeExpenseData?.totalIncome)) 
        ? Number(incomeExpenseData.totalIncome) 
        : 0,
      totalExpense: isFinite(Number(incomeExpenseData?.totalExpense)) && !isNaN(Number(incomeExpenseData?.totalExpense)) 
        ? Number(incomeExpenseData.totalExpense) 
        : 0,
      monthlyIncExp: monthlyIncExp,
    }
    
    console.log('📊 Cleaned income/expense data:', JSON.stringify(result, null, 2))
    return result
  }

  /**
   * Get cash flow data for specific branch and year
   */
  async getCashFlow(branchId: number, year: number): Promise<CashFlowData> {
    console.log(`📊 Fetching cash flow for branch ${branchId}, year ${year}`)
    
    const response = await apiClient.get<any>(
      endpoints.dashboards.cashFlow(branchId, year)
    )
    
    console.log('📊 RAW Cash flow data from API:', JSON.stringify(response.data, null, 2))
    
    // Handle different response structures
    let cashFlowData: any = response.data
    
    // Check if data is wrapped in a 'data' property
    if (cashFlowData && cashFlowData.data && typeof cashFlowData.data === 'object') {
      cashFlowData = cashFlowData.data
    }
    
    // Process monthly cash flow data
    let monthlyCashFlow = cashFlowData?.monthlyCashFlow || []
    
    if (Array.isArray(monthlyCashFlow)) {
      monthlyCashFlow = monthlyCashFlow.map((item: any) => ({
        year: Number(item.year) || year,
        month: Number(item.month) || 0,
        cashBalance: isFinite(Number(item.cashBalance)) && !isNaN(Number(item.cashBalance)) 
          ? Number(item.cashBalance) 
          : 0,
      })).filter(item => 
        item.month >= 1 && item.month <= 12 && 
        isFinite(item.cashBalance) && !isNaN(item.cashBalance)
      )
    }
    
    const result: CashFlowData = {
      openingCash: isFinite(Number(cashFlowData?.openingCash)) && !isNaN(Number(cashFlowData?.openingCash)) 
        ? Number(cashFlowData.openingCash) 
        : 0,
      totalIncoming: isFinite(Number(cashFlowData?.totalIncoming)) && !isNaN(Number(cashFlowData?.totalIncoming)) 
        ? Number(cashFlowData.totalIncoming) 
        : 0,
      totalOutgoing: isFinite(Number(cashFlowData?.totalOutgoing)) && !isNaN(Number(cashFlowData?.totalOutgoing)) 
        ? Number(cashFlowData.totalOutgoing) 
        : 0,
      closingCash: isFinite(Number(cashFlowData?.closingCash)) && !isNaN(Number(cashFlowData?.closingCash)) 
        ? Number(cashFlowData.closingCash) 
        : 0,
      monthlyCashFlow: monthlyCashFlow,
    }
    
    console.log('📊 Cleaned cash flow data:', JSON.stringify(result, null, 2))
    return result
  }

  /**
   * Get profit and loss data for specific branch and year
   */
  async getProfitLoss(branchId: number, year: number): Promise<ProfitLossData> {
    console.log(`📊 Fetching profit/loss for branch ${branchId}, year ${year}`)
    
    const response = await apiClient.get<any>(
      endpoints.dashboards.profitLoss(branchId, year)
    )
    
    console.log('📊 RAW Profit/Loss data from API:', JSON.stringify(response.data, null, 2))
    
    // Handle different response structures
    let profitLossData: any = response.data
    
    // Check if data is wrapped in a 'data' property
    if (profitLossData && profitLossData.data && typeof profitLossData.data === 'object') {
      profitLossData = profitLossData.data
    }
    
    // Process monthly profit/loss data
    let monthlyProfitLoss = profitLossData?.monthlyProfitLoss || []
    
    if (Array.isArray(monthlyProfitLoss)) {
      monthlyProfitLoss = monthlyProfitLoss.map((item: any) => ({
        year: Number(item.year) || year,
        month: Number(item.month) || 0,
        profit: isFinite(Number(item.profit)) && !isNaN(Number(item.profit)) 
          ? Number(item.profit) 
          : 0,
      })).filter(item => 
        item.month >= 1 && item.month <= 12 && 
        isFinite(item.profit) && !isNaN(item.profit)
      )
    }
    
    const result: ProfitLossData = {
      totalProfit: isFinite(Number(profitLossData?.totalProfit)) && !isNaN(Number(profitLossData?.totalProfit)) 
        ? Number(profitLossData.totalProfit) 
        : 0,
      monthlyProfitLoss: monthlyProfitLoss,
    }
    
    console.log('📊 Cleaned profit/loss data:', JSON.stringify(result, null, 2))
    return result
  }

  /**
   * Get HR dashboard data for specific branch
   */
  async getHRDashboard(branchId?: number): Promise<HRDashboardData> {
    console.log(`📊 Fetching HR dashboard for branch ${branchId || 'default'}`)
    
    const branchIdParam = branchId || 1 // Default to branch 1 if not provided
    const response = await apiClient.get<HRDashboardData>(
      endpoints.dashboards.hr(branchIdParam)
    )
    
    console.log('📊 HR dashboard data received:', response.data)
    return response.data
  }

  /**
   * Mock HR dashboard data for development when API is not available
   */
  getMockHRDashboard(): HRDashboardData {
    return {
      stats: {
        totalEmployees: {
          current: 245,
        },
        newHires: {
          thisMonth: 12,
        },
        onLeave: {
          current: 8,
          scheduled: 15,
        },
        attritionRate: {
          current: 3.2,
          target: 5.0,
        },
      },
      departmentChart: [
        { name: 'Engineering', value: 45 },
        { name: 'Sales', value: 32 },
        { name: 'Marketing', value: 28 },
        { name: 'HR', value: 15 },
        { name: 'Finance', value: 20 },
        { name: 'Operations', value: 35 },
      ],
      recentActivity: [
        {
          id: '1',
          type: 'Employee',
          description: 'New employee hired: John Doe',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          userId: 'user1',
          userName: 'Admin',
        },
        {
          id: '2',
          type: 'Employee',
          description: 'Employee updated: Jane Smith',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          userId: 'user2',
          userName: 'HR Manager',
        },
      ],
    }
  }
}

export const dashboardsService = new DashboardsService()
